import interact from 'interact.js';
import { Controller } from 'stimulus';
import Util from '@/js/editor/util';
import * as ItemReplace from '@/js/editor/item-replace';
import { createOutline, removeOutline } from '@/js/editor/outline-events';

// Counted per replace. The canvas Cmd-drag flow counts item_replaced_by_canvas_drag instead, so
// the two are never mixed and each flow's usage stands on its own.
const REPLACE_FEATURE_ACTION = 'item_replaced';

// Outlines this flow owns: the incoming item's preview, and the dwell countdown's target ring.
const PREVIEW_OUTLINE_ID = 'preloaded-preview-outline';
const TARGET_OUTLINE_ID = 'replace-target-outline';

const REPLACE_HOVER_MS = 750;

// How long the pointer must hold still before the countdown appears
const SETTLE_MS = 120;

export default class extends Controller {
  static classes = ['ghostThumb'];

  static values = {
    recordId: Number,
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // Lifecycle
  // ═══════════════════════════════════════════════════════════════════════════

  connect() {
    interact(this.element)
      .draggable({
        manualStart: true,
        onmove: (event) => {
          this.dragGhostThumb(event);
        },
        onend: (event) => {
          this.endGhostDrag(event);
        },
      })
      .on('move', (event) => {
        this.startGhostDrag(event);
      });

    this._onClickToAddHandler = this._onClickToAdd.bind(this);
    this.element.addEventListener('click', this._onClickToAddHandler);
  }

  disconnect() {
    if (this.scrollableContainer) {
      this.restoreContainerScroll();
      this.scrollableContainer = null;
    }
    if (this._onClickToAddHandler) {
      this.element.removeEventListener('click', this._onClickToAddHandler);
    }
    // Capture before teardown nulls these; only clear the SHARED drag UI if this controller
    // was the one mid-drag (e.g. disconnected via Turbo frame swap), not a drag another owns.
    const hadDragState = Boolean(this._dragKeyHandler || this._hoverTimer || this._hoverItem);
    this.#teardownReplaceModifier();
    this.#clearHoverTimers();
    this._hoverItem = null;
    // If we disconnect mid-preview (e.g. a Turbo swap of the panel), restore the hidden target and drop the preloaded node.
    this.#clearHiddenReplaceTarget();
    document.querySelector('.item-preloaded')?.remove();
    this.#clearPreloadedState();
    if (hadDragState) {
      this.#hideReplaceHintPopup();
      document.getElementById('editor-container')?.classList.remove('dragging-in-progress');
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Click-to-Add Flow
  // ═══════════════════════════════════════════════════════════════════════════

  async _onClickToAdd(event) {
    // Suppress the synthetic click some browsers emit at the end of a drag.
    if (this._didDrag) {
      this._didDrag = false;
      return;
    }
    // Let nested interactive elements (carousel, magnifier, favourite, lock) keep their behaviour.
    if (event.target.closest('button, a, form')) {
      return;
    }
    // Prevent overlapping click-adds on the same thumb.
    if (this._addingToCanvas) {
      return;
    }

    this._addingToCanvas = true;
    this.element.classList.add('opacity-50');
    document.dispatchEvent(new CustomEvent('itemAdded'));
    try {
      await this._addItemAtCanvasCenter();
    } finally {
      this._addingToCanvas = false;
      this.element.classList.remove('opacity-50');
    }
  }

  async _addItemAtCanvasCenter() {
    const ed = window.ed;

    // Stagger chain: first empty slot from centre outwards. An entry is "free"
    // when the item is gone, soft-deleted, or has been moved from its placed coords.
    const chain = ed._clickAddChain || (ed._clickAddChain = []);
    let slot = 0;
    while (slot < chain.length) {
      const entry = chain[slot];
      const stillPlaced =
        entry &&
        entry.item.isConnected &&
        entry.item.classList.contains('item') &&
        entry.item.e._objProps.realX === entry.realX &&
        entry.item.e._objProps.realY === entry.realY;
      if (!stillPlaced) {
        break;
      }
      slot += 1;
    }

    // Add at (0,0); we'll re-position via realX/realY (container px) after dimensions are known.
    const itemProps = { id: this.recordIdValue, x: 0, y: 0, newlyAdded: true, selectItem: false };
    const item = await this.addItem(itemProps);
    if (!item) {
      return;
    }

    // Stagger using the *visible* top-left so capped items line up with uncapped ones.
    // visible_left = realX + width*(1 - scale)/2; visible Y analogous.
    const props = item.e._objProps;
    const capScale = this.#scaleFromTransform(props.transform);
    const anchorItem = slot > 0 ? chain[slot - 1].item : null;
    const anchorVisible = anchorItem ? this.#visibleTopLeft(anchorItem) : null;
    const visibleX = anchorVisible
      ? anchorVisible.x + 40
      : (ed.containerWidth - props.width * capScale) / 2;
    const visibleY = anchorVisible
      ? anchorVisible.y + 40
      : (ed.containerHeight - props.height * capScale) / 2;
    props.realX = visibleX - (props.width * (1 - capScale)) / 2;
    props.realY = visibleY - (props.height * (1 - capScale)) / 2;
    // newlyAdded:false — re-apply position/transform from new realX/realY without re-running the cap.
    ed.updateSingleItemZoomRatio(item, false);

    chain[slot] = { item, realX: props.realX, realY: props.realY };

    ed._shared.unselectItems();
    ed._shared.selectItem(item, false);
    ed._history.setState();
    ed._shared.setUnloadConfirmation();
    ed._interaction.refreshHandles();

    // shared.addItem dispatches this only when selectItem:true; we passed false, so fire it for text.
    if (props.kind === 'text') {
      document.dispatchEvent(new CustomEvent('legacy:text-editor-requested', { detail: { item } }));
    }
  }

  // Extract scaleX from a matrix3d transform string (column-major). hypot handles rotation/skew correctly.
  #scaleFromTransform(transformStr) {
    if (!transformStr) {
      return 1;
    }
    const parts = transformStr.split(',');
    const scale = Math.hypot(Number.parseFloat(parts[0]) || 0, Number.parseFloat(parts[1]) || 0);
    return Number.isFinite(scale) && scale > 0 ? scale : 1;
  }

  // Visible top-left in container px, derived from current props (not stored) so resizes are honoured.
  #visibleTopLeft(item) {
    const p = item.e._objProps;
    const scale = this.#scaleFromTransform(p.transform);
    return {
      x: p.realX + (p.width * (1 - scale)) / 2,
      y: p.realY + (p.height * (1 - scale)) / 2,
    };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Drag Flow
  // ═══════════════════════════════════════════════════════════════════════════

  createGhostElement() {
    throw new Error('descendant class should implement createGhostElement method');
  }

  startGhostDrag(event) {
    const { interaction } = event;
    if (interaction.pointerIsDown && !interaction.interacting()) {
      this.#addPreloadedItem();
      // Default action is "add". Replace turns on by hovering one item for REPLACE_HOVER_MS
      // (see dragGhostThumb) or by holding the platform modifier (⌘ on Mac, Ctrl elsewhere).
      document.getElementById('editor-container').classList.add('dragging-in-progress');
      this._hoverItem = null;
      this._hoverElapsed = false;
      this._modifierHeld = false;
      // Key events let the modifier react even while the pointer is still.
      this._dragKeyHandler = this.#onDragKey.bind(this);
      document.addEventListener('keydown', this._dragKeyHandler);
      document.addEventListener('keyup', this._dragKeyHandler);

      const element = this.createGhostElement();
      element.classList.value = this.ghostThumbClasses.join(' ');
      const sourceRect = event.currentTarget.getBoundingClientRect();

      // Append to <body> with position:fixed so the ghost can render above the drawer
      // (the canvas lives inside editor-layer-editor's stacking context).
      element.style.position = 'fixed';
      element.style.left = `${sourceRect.left}px`;
      element.style.top = `${sourceRect.top}px`;
      element.e = { x: sourceRect.left, y: sourceRect.top };

      document.body.appendChild(element);

      Object.assign(event.interactable, { distX: 0, distY: 0 });

      // disable scroll events during drag
      this.scrollableContainer = this.findOriginContainer();
      this.preventContainerScroll();

      interaction.start({ name: 'drag' }, event.interactable, element);
    }
  }

  dragGhostThumb(event) {
    const { target, interactable, dx, dy } = event;
    const editorContainer = document.getElementById('editor-container');
    if (editorContainer.classList.contains('dragging-in-progress')) {
      // Honour the modifier from the move event too, in case it was already held when the
      // drag began (no keydown fired in that case).
      const modifierHeld = ItemReplace.isModifierHeld(event);
      if (modifierHeld !== this._modifierHeld) {
        this.#setModifierHeld(modifierHeld);
      }

      // The ghost thumb is pointer-events:none, so this returns the item beneath it.
      const overItem =
        document
          .elementFromPoint(event.clientX, event.clientY)
          ?.closest(ItemReplace.REPLACEABLE_TARGET) || null;

      if (overItem !== this._hoverItem) {
        // New item: start fresh in "add" mode and (re)start the dwell timer.
        this.#setHoverItem(overItem);
      } else if (overItem && (dx || dy)) {
        // Same item: any movement at all cancels a dwell-activated replace and restarts the timer,
        // so the user can nudge to drop on top instead of replacing (the modifier path is immune).
        // Clear the elapsed flag first: the restarted wait means replace is pending again,
        // and #restartReplaceWait only marks the target while that is true.
        const wasElapsed = this._hoverElapsed;
        this._hoverElapsed = false;
        this.#restartReplaceWait();
        if (wasElapsed) {
          this.#applyReplaceState();
        }
      }
    }

    if (Util.isDefined(interactable.distX)) {
      // Update distances
      interactable.distX += dx;
      interactable.distY += dy;

      const absDistX = Math.abs(interactable.distX);
      const absDistY = Math.abs(interactable.distY);
      const isDragHorizontallyHigher = absDistX <= absDistY + 5;

      if (!Util.isTouchScreen() || isDragHorizontallyHigher) {
        target.style.display = 'block';
      }

      if (absDistX > 10 || absDistY > 10 || isDragHorizontallyHigher) {
        delete interactable.distX;
        delete interactable.distY;
      }
    } else {
      target.style.display = 'block';
    }

    target.e.x += dx;
    target.e.y += dy;

    target.style.left = `${target.e.x}px`;
    target.style.top = `${target.e.y}px`;
  }

  endGhostDrag(event) {
    const { target } = event;
    this.#clearHoverTimers();
    this._hoverItem = null;
    this.#hideReplaceHintPopup();
    this.#teardownReplaceModifier();

    // Hit-test before clearing the class — it makes .item-overlay pointer-events:none.
    // clientX/Y (not pageX/Y) because elementFromPoint takes viewport coordinates.
    const droppedOverElement = document.elementFromPoint(event.clientX, event.clientY);
    document.getElementById('editor-container').classList.remove('dragging-in-progress');
    const moodboardRightPanel = document.getElementById('moodboard-right-panel');
    const isRightPanel = Boolean(
      droppedOverElement && moodboardRightPanel && moodboardRightPanel.contains(droppedOverElement)
    );
    target.remove();

    if (this.scrollableContainer) {
      // re-enable scroll events
      this.restoreContainerScroll();
      this.scrollableContainer = null;
    }

    // Suppress the synthetic click some browsers emit after a drag; setTimeout clears it next tick.
    this._didDrag = true;
    setTimeout(() => {
      this._didDrag = false;
    }, 0);

    // disable drop functionality on right panel to prevent dropping items on canvas during touch scrolling
    if (isRightPanel) {
      // Clean up replacement preview state
      this.#clearHiddenReplaceTarget();
      removeOutline(PREVIEW_OUTLINE_ID);
      document.querySelector('.item-preloaded')?.remove();
      this.#clearPreloadedState();
      return;
    }

    document.dispatchEvent(new CustomEvent('itemAdded'));
    // target.e is viewport-relative (ghost on body); convert to canvas-relative for addItem.
    const canvasRect = window.ed.boardContainer.getBoundingClientRect();
    const itemProps = {
      id: this.recordIdValue,
      x: target.e.x - canvasRect.left,
      y: target.e.y - canvasRect.top,
      centerItem: true,
      newlyAdded: true,
    };

    // If replacement preview is active, promote the preloaded item and delete target.
    if (this._hiddenReplaceTarget) {
      const preloaded = document.querySelector('.item-preloaded');
      if (!preloaded) {
        this.#clearHiddenReplaceTarget();
        this.addItem(itemProps);
        removeOutline(PREVIEW_OUTLINE_ID);
        return;
      }
      const wasSelected = this._hiddenReplaceTarget.classList.contains('selected');
      const oldProps = this._hiddenReplaceTarget.e._objProps;
      const oldBoardItemId = oldProps.boardItemId;
      preloaded.classList.remove('item-preloaded', 'pointer-events-none');
      preloaded.classList.add('item');
      preloaded.style.opacity = '';

      const newBoardItemId = preloaded.e._objProps.boardItemId;
      const outline = document.querySelector(`[data-outline-reference="${oldBoardItemId}"]`);
      if (outline) {
        outline.dataset.outlineReference = newBoardItemId;
      }
      // Update the outline controller's internal tracking Set
      const outlineController = document.querySelector('[data-controller~="moodboard--outline"]');
      if (outlineController) {
        const ctrl = this.application.getControllerForElementAndIdentifier(
          outlineController,
          'moodboard--outline'
        );
        if (ctrl && ctrl.outlinedItems) {
          ctrl.outlinedItems.delete(oldBoardItemId);
          if (outline) {
            ctrl.outlinedItems.add(newBoardItemId);
          }
        }
      }

      this._hiddenReplaceTarget.style.opacity = '';
      if (wasSelected) {
        window.ed._shared.removeItem(this._hiddenReplaceTarget);
      } else {
        window.ed._shared.removeItemKeepingSelection(this._hiddenReplaceTarget);
      }
      this._hiddenReplaceTarget = null;
      ItemReplace.recordReplace(REPLACE_FEATURE_ACTION);
      if (wasSelected) {
        this.#selectAndPlaceOverlay(preloaded);
      }
      removeOutline(PREVIEW_OUTLINE_ID);
    } else {
      const preloaded = document.querySelector('.item-preloaded');
      if (preloaded) {
        this.#usePreloadedItem(preloaded, itemProps);
        this.#clearPreloadedState();
      } else if (this._preloadedItemPromise) {
        // If preloaded item still loading when thumb is dropped — wait for it to finish. Happens on quick drop.
        const pendingPromise = this._preloadedItemPromise;
        this._preloadedDropPending = true;
        pendingPromise
          .then((item) => {
            if (!item) {
              // Preloaded add failed (e.g. image fetch errored); nothing to promote.
              return;
            }
            this.#usePreloadedItem(item, itemProps); // use preloaded item when thumb is dropped
          })
          .finally(() => {
            if (this._preloadedItemPromise === pendingPromise) {
              this.#clearPreloadedState();
            }
          });
      } else {
        this.addItem(itemProps);
      }
    }
    if (!this._preloadedDropPending) {
      this.#clearPreloadedState();
    }
  }

  findOriginContainer() {
    let element = this.element;
    while (element && element !== document.body) {
      const style = window.getComputedStyle(element);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        return element;
      }
      element = element.parentElement;
    }
    return document.body;
  }

  preventContainerScroll() {
    if (!this.scrollableContainer) {
      return;
    }
    this.scrollableContainer.style.overflow = 'hidden';
  }

  restoreContainerScroll() {
    if (!this.scrollableContainer) {
      return;
    }
    this.scrollableContainer.style.overflow = 'auto';
  }

  #onDragKey(event) {
    const editorContainer = document.getElementById('editor-container');
    if (!editorContainer) {
      return;
    }
    const held = ItemReplace.isModifierHeld(event);
    if (held !== this._modifierHeld) {
      this.#setModifierHeld(held);
    }
  }

  #selectAndPlaceOverlay(item) {
    window.ed._shared.selectItem(item);
    window.ed._interaction.refreshHandles();
  }

  #usePreloadedItem(preloaded, itemProps) {
    const ed = window.ed;

    // restore dimensions if replacement preview distorted them.
    const repainted = this.#restorePreloadedState(preloaded)
      ? ItemReplace.redrawIfCanvas(preloaded)
      : Promise.resolve();

    const props = preloaded.e._objProps;
    props.realX = itemProps.x;
    props.realY = itemProps.y;
    props.centerItem = true;
    preloaded.e.calculateDimensions();
    ed.updateSingleItemZoomRatio(preloaded, false); // properly position item
    preloaded.classList.remove('item-preloaded', 'pointer-events-none');
    preloaded.classList.add('item');

    repainted.then(() => {
      preloaded.style.opacity = '';
      this.#selectAndPlaceOverlay(preloaded);
    });

    if (preloaded.e.kind === 'text') {
      document.dispatchEvent(
        new CustomEvent('legacy:text-editor-requested', { detail: { item: preloaded } })
      );
    }

    ed._history.setState();
    ed._shared.setUnloadConfirmation();
    ed._shared.hideBlankCanvasMessage();
    ed._shared.toggleTemplatesButton();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Replace Logic
  // ═══════════════════════════════════════════════════════════════════════════

  #addPreloadedItem() {
    const result = this.addItem({
      id: this.recordIdValue,
      x: -window.ed.containerWidth,
      y: -window.ed.containerHeight,
      newlyAdded: true,
      selectItem: false,
    });
    // Subclass addItem() may not return a Promise (e.g. swatch recolor path),
    // so wrap to avoid "Cannot read properties of undefined (reading 'then')".
    this._preloadedItemPromise = Promise.resolve(result).then((item) => {
      if (!item) {
        return;
      }
      const editorContainer = document.getElementById('editor-container');
      if (
        !editorContainer?.classList.contains('dragging-in-progress') &&
        !this._preloadedDropPending
      ) {
        item.remove();
        return;
      }
      const existing = document.querySelector('.item-preloaded');
      if (existing) {
        existing.remove();
      }
      item.classList.remove('item');
      item.classList.add('item-preloaded', 'pointer-events-none');
      item.style.opacity = '0';

      // If replace conditions are already active, show the preview immediately
      // rather than waiting for the next dragmove event.
      if (this._hoverItem && (this._hoverElapsed || this._modifierHeld)) {
        this.#applyReplaceState();
      }
      return item;
    });
  }

  #applyReplaceState() {
    const replace = Boolean(this._hoverItem) && (this._hoverElapsed || this._modifierHeld);
    if (!this.#isReplacePending()) {
      this.#hideReplaceHintPopup();
    }
    const ghostThumb = document.querySelector('.ghost-thumb');
    const preloaded = document.querySelector('.item-preloaded');
    if (replace && preloaded) {
      // Already positioned for this target — skip expensive recomputation.
      if (this._hiddenReplaceTarget === this._hoverItem) {
        return;
      }
      if (this._hiddenReplaceTarget) {
        this._hiddenReplaceTarget.style.opacity = '';
      }
      const target = this._hoverItem;
      this._hiddenReplaceTarget = target;
      if (ghostThumb) {
        ghostThumb.style.visibility = 'hidden';
      }
      preloaded.style.opacity = '0';
      this.#positionPreloadedAsReplacement(preloaded, target).then(() => {
        if (this._hiddenReplaceTarget !== target) {
          return;
        }
        target.style.opacity = '0';
        preloaded.style.opacity = '';
        createOutline(PREVIEW_OUTLINE_ID, {
          item: preloaded,
          classes: ItemReplace.PREVIEW_OUTLINE_CLASSES,
        });
      });
    } else {
      this.#clearHiddenReplaceTarget();
      if (ghostThumb) {
        ghostThumb.style.visibility = '';
      }
      if (preloaded) {
        preloaded.style.opacity = '0';
        preloaded.style.left = `${-window.ed.containerWidth}px`;
        preloaded.style.top = `${-window.ed.containerHeight}px`;
      }
      removeOutline(PREVIEW_OUTLINE_ID);
    }
  }

  #setHoverItem(overItem) {
    this._hoverItem = overItem;
    this._hoverElapsed = false;
    if (overItem) {
      this.#restartReplaceWait();
    } else {
      this.#clearHoverTimers();
    }
    this.#applyReplaceState();
  }

  #setModifierHeld(held) {
    this._modifierHeld = held;
    // Releasing the modifier hands the decision back to the dwell timer. Restart it so the
    // countdown the user is shown matches the wait that is actually left.
    if (!held && this._hoverItem && !this._hoverElapsed) {
      this.#restartReplaceWait();
    }
    this.#applyReplaceState();
  }

  #restartReplaceWait() {
    this.#clearHoverTimers();
    this.#hideReplaceHintPopup();
    if (!this.#isReplacePending()) {
      return;
    }
    this._settleTimer = setTimeout(() => {
      this._settleTimer = null;
      this.#startDwellTimer();
    }, SETTLE_MS);
  }

  #startDwellTimer() {
    this.#showReplaceHintPopup();
    this._hoverTimer = setTimeout(() => {
      this._hoverTimer = null;
      this._hoverElapsed = true;
      this.#applyReplaceState();
    }, REPLACE_HOVER_MS);
  }

  #positionPreloadedAsReplacement(preloaded, target) {
    this.#restorePreloadedState(preloaded);
    this._preloadedOriginalProps = ItemReplace.capturePlacement(preloaded);
    this._preloadedOriginalVisuals = ItemReplace.captureVisuals(preloaded);
    ItemReplace.mirrorTargetVisuals(preloaded, target);

    return ItemReplace.fitToTargetBounds(preloaded, target);
  }

  // Undoes a preview: the item goes back to its own geometry and its own look. Every path that
  // promotes the preloaded item as a plain drop comes through here, so a preview the user backed
  // out of cannot leave the target's look on the item that lands. A committed replace
  // deliberately does not come through here — there, keeping the target's look is the point.
  #restorePreloadedState(preloaded) {
    const placement = this._preloadedOriginalProps;
    const visuals = this._preloadedOriginalVisuals;
    this._preloadedOriginalProps = null;
    this._preloadedOriginalVisuals = null;
    ItemReplace.restoreVisuals(preloaded, visuals);

    return ItemReplace.restorePlacement(preloaded, placement);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Replace Hint Popup
  // ═══════════════════════════════════════════════════════════════════════════

  // True while a drop would still land on top, but resting a moment longer would replace
  // instead. That window is what the popup counts down.
  #isReplacePending() {
    return Boolean(this._hoverItem) && !this._hoverElapsed && !this._modifierHeld;
  }

  #showReplaceHintPopup() {
    if (!this.#isReplacePending()) {
      return;
    }
    // One measurement feeds both the outline and the popup, so the two cannot disagree about
    // where the item is. These corners already account for item scale, rotation and board zoom.
    const targetCorners = window.ed._interaction.getCornersCoordinates(this._hoverItem);
    this.#setPendingTarget(this._hoverItem, targetCorners);
    document.dispatchEvent(
      new CustomEvent('moodboard--replace-hint-popup:show', {
        detail: {
          durationMs: REPLACE_HOVER_MS,
          modifierLabel: ItemReplace.MODIFIER_LABEL,
          targetCorners,
        },
      })
    );
  }

  #hideReplaceHintPopup() {
    if (!this._hintPopupShown) {
      return;
    }
    this.#setPendingTarget(null);
    document.dispatchEvent(new CustomEvent('moodboard--replace-hint-popup:hide'));
  }

  #setPendingTarget(target, targetCorners) {
    this._hintPopupShown = Boolean(target);
    if (!target) {
      removeOutline(TARGET_OUTLINE_ID);
      return;
    }

    createOutline(TARGET_OUTLINE_ID, { corners: targetCorners });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Shared
  // ═══════════════════════════════════════════════════════════════════════════

  #teardownReplaceModifier() {
    if (this._dragKeyHandler) {
      document.removeEventListener('keydown', this._dragKeyHandler);
      document.removeEventListener('keyup', this._dragKeyHandler);
      this._dragKeyHandler = null;
    }
    this._modifierHeld = false;
  }

  #clearHiddenReplaceTarget() {
    if (this._hiddenReplaceTarget) {
      this._hiddenReplaceTarget.style.opacity = '';
      this._hiddenReplaceTarget = null;
    }
  }

  #clearPreloadedState() {
    this._preloadedItemPromise = null;
    this._preloadedOriginalProps = null;
    this._preloadedOriginalVisuals = null;
    this._preloadedDropPending = false;
  }

  #clearHoverTimers() {
    if (this._settleTimer) {
      clearTimeout(this._settleTimer);
      this._settleTimer = null;
    }
    if (this._hoverTimer) {
      clearTimeout(this._hoverTimer);
      this._hoverTimer = null;
    }
  }

  addItem(itemProps) {
    const selectItem = itemProps.selectItem ?? true;
    delete itemProps.selectItem;
    return window.ed._shared.addItem({
      e: itemProps,
      selectItem,
      updateSingleItemZoomRatio: true,
    });
  }
}
